'use client';

import { useIdentity } from '@hyphengroup/cms-components/modules/auth';
import type { CmsAudience } from '@hyphengroup/cms-components/utils/audience';
import type { JSX, ReactNode } from 'react';

/**
 * Audience-visibility gate for CMS blocks (CMS-1794). Renders `children` only
 * when the block's editor-set `audience` matches the viewer's login state.
 *
 * Login is read client-side via `useIdentity()` — the existing `id_token`
 * signal (same one the header uses), reused rather than duplicated. The gate
 * only wraps blocks whose `audience` is not `ALL` (see BlockMapper), so ordinary
 * blocks keep their server render untouched. The decision resolves after
 * hydration, which is fine on the purchase-success page (already fully
 * client-resolved: skeleton card + token text blocks fill in post-hydration).
 *
 * SSR + first client render: `useIdentity()` is undefined (treated as guest),
 * so a `LOGGED_OUT` block shows and a `LOGGED_IN` block is hidden; it settles
 * once the cookie is read on the client.
 */
export const AudienceGate = ({
  audience,
  children,
}: {
  audience?: CmsAudience | null;
  children: ReactNode;
}): JSX.Element | null => {
  const loggedIn = !!useIdentity();
  if (audience === 'LOGGED_IN' && !loggedIn) return null;
  if (audience === 'LOGGED_OUT' && loggedIn) return null;
  return <>{children}</>;
};
